import React from 'react';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { get } from 'lodash-es';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';

// Models
import { VehicleModel } from 'client/data/models/vehicle';
import { CmsModel } from 'client/data/models/cms';
import { SEOModel } from 'client/data/models/seo';
import { DealerInventoryModel } from 'client/data/models/dealer-inventory';
import { ReviewsModel, ReviewsPaths } from 'client/data/models/dealer-reviews';
import { DealerInfoModel, getDealerInfoNamePath } from 'client/data/models/dealer-info';
import { buildDealerIcoWidgetConfigurationPath, DealerIcoWidgetModel } from 'client/data/models/dealer-ico-widget';

// Constants
import { HTTP_NOT_FOUND } from 'client/utils/http-status';
import { DEFAULT_REVIEWS_TYPE, REVIEWS_SALES, REVIEWS_SERVICE } from 'client/constants/dealer-reviews';

// Utils
import { setCriticalCss } from 'client/actions/critical-css';
import { getTagsFromPhotosArray, groupPhotosByTag } from 'site-modules/dealerships/utils/dealer-photos';
import { getOverallRating } from 'site-modules/dealerships/utils/dealer-rating';
import { showChatButton } from 'site-modules/dealerships/utils/show-chat-button';
import { createSEOContent } from 'site-modules/dealerships/pages/dealer/dealer-seo';
import { updateDealer } from 'client/actions/dealer';
import { getQuery } from 'client/utils/location';

// Components
import { EdmundsCpaNoAdhesionDecorator } from 'site-modules/shared/pages/decorators/edmunds-cpa-no-adhesion';
import { DealerDetailsPage, getDealerModelPath } from 'site-modules/dealerships/pages/dealer/dealer';

export default params =>
  pageDefinition({
    name: 'car_inventory_ddp_map',
    category: 'car_inventory',
    preload: (store, props) => {
      const { page = '1', type } = getQuery(props.location);
      const logicalName = decodeURIComponent(get(params, 'logicalName', ''));

      const preloader = new ModelPreloader(store);
      preloader.resolve('makesList', VehicleModel);
      preloader.resolve('content["editorial/includes/dywai-footer-links"]', CmsModel);
      preloader.resolve('pageProfile', SEOModel);
      preloader.resolve(getDealerModelPath('dealerInventory', { ...props, params }), DealerInventoryModel);

      preloader.set(ReviewsPaths.getReviewsFilterPath(), ReviewsModel, { page, type });

      preloader.resolve(getDealerInfoNamePath(logicalName), DealerInfoModel);

      return preloader.load().then(modelState => {
        const dealerInfo = modelState.get(getDealerInfoNamePath(logicalName), DealerInfoModel);

        const dealerInventory = modelState.get(
          getDealerModelPath('dealerInventory', { ...props, params }),
          DealerInventoryModel
        );
        const dealerInvCount = get(dealerInventory, 'rooftopCounters.total', 0);

        if (!dealerInfo) {
          const ex = new Error(`ValidatorError - Could not find a dealer - ${logicalName}`);
          ex.status = HTTP_NOT_FOUND;
          ex.redirectPageTo404 = true;
          throw ex;
        }
        const dealerId = dealerInfo.rooftopId.toString();
        preloader.resolve(ReviewsPaths.getTopReviewsPath({ dealerId }), ReviewsModel);
        preloader.resolve(ReviewsPaths.getOverallRatings({ dealerId }), ReviewsModel);
        preloader.resolve(buildDealerIcoWidgetConfigurationPath({ rooftopId: dealerId }), DealerIcoWidgetModel);

        store.dispatch(
          updateDealer({
            rooftopId: dealerId,
          })
        );

        preloader.resolve(`carcodeConfig.${dealerId}`, DealerInfoModel);

        return preloader.load().then(stateModel => {
          const reviewsByType = modelState.get(ReviewsPaths.getTopReviewsPath({ dealerId }), ReviewsModel);
          const defaultReviewsType =
            reviewsByType && (reviewsByType[REVIEWS_SALES] || reviewsByType[REVIEWS_SERVICE])
              ? DEFAULT_REVIEWS_TYPE
              : REVIEWS_SALES;
          const reviewsType =
            get(modelState.get(ReviewsPaths.getReviewsFilterPath(), ReviewsModel), 'type') || defaultReviewsType;

          preloader.resolve(
            ReviewsPaths.getDealershipReviewsByTypePath({
              type: reviewsType,
              dealerId,
            }),
            ReviewsModel
          );

          if (!dealerInvCount) {
            store.dispatch(setCriticalCss(false));
          } else {
            const { showroomPhotos, logoUrl, fiveStarDealerAward } = dealerInfo;
            const hasFiveStarDealerAward = !!(fiveStarDealerAward && fiveStarDealerAward.length);
            const hasShowroomPhotos = !!(showroomPhotos && showroomPhotos.length);
            let tagList;
            let photosByTag;
            let photos = false;

            if (hasShowroomPhotos) {
              photos = 'photos';
              tagList = getTagsFromPhotosArray(showroomPhotos);
              photosByTag = groupPhotosByTag({ showroomPhotos, logoUrl, tagList });
              const description = photosByTag.DEFAULT[0].description;

              if (tagList && !!tagList.length) {
                photos = 'photosTags';
              }

              if (description) photos += 'Description';
            }

            const overallRatingByType = stateModel.get(ReviewsPaths.getOverallRatings({ dealerId }), ReviewsModel);
            const overallRating = getOverallRating(overallRatingByType, reviewsType);

            const state = store.getState();
            const isMobile = state.mobile;
            const dealerInfoCarcode = stateModel.get(`carcodeConfig.${dealerId}`, DealerInfoModel);

            const icoWidgetConfig = stateModel.get(
              buildDealerIcoWidgetConfigurationPath({ rooftopId: dealerId }),
              DealerIcoWidgetModel
            );

            const hasIco = get(icoWidgetConfig, 'isActive', false);

            const dealerSmsNumber = get(dealerInfoCarcode, 'smsNumber');
            const isEdmundsChatActive = get(dealerInfoCarcode, 'isEdmundsChatActive');
            const parentDealershipName = get(dealerInfoCarcode, 'parentDealershipName');

            const hasChatButton = showChatButton({
              isMobile,
              parentDealershipName,
              dealerSmsNumber,
              isEdmundsChatActive,
            });

            store.dispatch(
              setCriticalCss({
                photos, // 4 values
                hasReviews: !!(overallRating && overallRating.totalCount), // 2 values
                hasFiveStarDealerAward, // 2 values
                hasChatButton, // 2 values
                hasIco, // 2 values (true, false)
              })
            );
          }
          return preloader.load();
        });
      });
    },
    seo: createSEOContent,
    component: props => <DealerDetailsPage {...props} params={params} />,
    decorator: EdmundsCpaNoAdhesionDecorator,
  });
